// 
// NOTE: Set User Load States in UserBootstrap!!!
//

import React from 'react';
import { createApiClient } from "./useApiClient";
import { UserRecord, ClientAccess, DimensionUnitType, DimensionDisplayType, UserSettingsPreferencesDto } from "../models/UserProfile";
import type { ClientLicenseAccessDto } from "../models/License";

interface UserCtx {
    // auth provider
    getAccessToken: () => Promise<string | null>;

    // current user
    user: UserRecord | null;
    setUser: (u: UserRecord | null) => void;

    // the selected/active client
    activeClientId: number | null;
    setActiveClientId: (id: number | null) => void;
    activeClient: ClientAccess | null;
    activeClientLicense: ClientLicenseAccessDto | null;

    //Active Tenant
    activeTenantId: number | null;
    setActiveTenantId: (id: number | null) => void;

    refreshUser: () => Promise<UserRecord>;
    invalidateUser: () => void;

    getClientInfo: (clients: ClientAccess[] | undefined, targetClientId: number | null) => ClientAccess | null;

    //Client Level Role
    setHasClientLevelRole: (access: boolean | null) => void;
    hasClientLevelRole: boolean | null;
    setDefaultPortfolioId: (id: number | null) => void;
    defaultPortfolioId: number | null;

    //Admin Roles
    isAdmin: boolean | null;
    isOwner: boolean | null;
    isValuer: boolean | null;
    isSysAdmin: boolean | null;

    //Finance  Role
    isFinance: boolean | null;

    //License State
    isViewOnly: boolean | null;
    setIsViewOnly: (s: boolean | null) => void;

    //Preferences
    compactView: boolean;
    showValues: boolean;
    dimensionUnitPreference: DimensionUnitType;
    dimensionDisplayPreference: DimensionDisplayType;
    updateUserSettings: (
        settings: Partial<UserSettingsPreferencesDto>
    ) => Promise<void>;
}

const Ctx = React.createContext<UserCtx | undefined>(undefined);

export const UserProvider: React.FC<React.PropsWithChildren<{
    getAccessToken: () => Promise<string | null>;
}>> = ({ children, getAccessToken }) => {

    const [user, setUser] = React.useState<UserRecord | null>(null);
    const [activeClientId, setActiveClientId] = React.useState<number | null>(null);
    const [activeClient, setActiveClient] = React.useState<ClientAccess | null>(null);
    const [activeClientLicense, setActiveClientLicense] = React.useState<ClientLicenseAccessDto | null>(null);
    const [isAdmin, setIsAdmin] = React.useState<boolean | null>(null);
    const [isOwner, setIsOwner] = React.useState<boolean | null>(null);
    const [isValuer, setIsValuer] = React.useState<boolean | null>(null);
    const [hasClientLevelRole, setHasClientLevelRole] = React.useState<boolean | null>(null);
    const [defaultPortfolioId, setDefaultPortfolioId] = React.useState<number | null>(null);
    const [isSysAdmin, setIsSysAdmin] = React.useState<boolean | null>(null);
    const [isViewOnly, setIsViewOnly] = React.useState<boolean | null>(null);
    const [isFinance, setIsFinance] = React.useState<boolean | null>(null);
    const [activeTenantId, setActiveTenantId] = React.useState<number | null>(null);
    const [compactView, setCompactView] = React.useState(false);
    const [showValues, setShowValues] = React.useState(false);
    const [dimensionUnitPreference, setDimensionUnitPreference] = React.useState(DimensionUnitType.Metric);
    const [dimensionDisplayPreference, setDimensionDisplayPreference] = React.useState(DimensionDisplayType.Unframed);

    const api = React.useMemo(
        () => createApiClient({ getAccessToken, getClientId: () => activeClientId, getTenantId: () => activeTenantId }, { timeoutMs: 12000 }),
        [getAccessToken, activeClientId, activeTenantId]
    );

    const debounceRef = React.useRef<number | null>(null);
    const bcRef = React.useRef<BroadcastChannel | null>(null);

    React.useEffect(() => {
        bcRef.current = "BroadcastChannel" in window ? new BroadcastChannel("user") : null;
        const onMsg = (e: MessageEvent) => {
            if (e.data?.type === "user:changed") scheduleRefresh();
        };
        bcRef.current?.addEventListener("message", onMsg);

        const onCustom = () => scheduleRefresh();
        window.addEventListener("user:changed", onCustom);

        return () => {
            bcRef.current?.removeEventListener("message", onMsg);
            bcRef.current?.close();
            window.removeEventListener("user:changed", onCustom);
        };
    }, []);

    React.useEffect(() => {
        localStorage.setItem('activeClientId', activeClientId?.toString() ?? '');
    }, [activeClientId]);

    const pickDefaultClientId = (u?: UserRecord | null) =>
        u?.defaultClientId ??
        u?.clients?.[0]?.clientId ??
        null;

    const getClientInfo = (clients: ClientAccess[] | undefined, targetClientId: number | null) => {

        if (clients && targetClientId != null) {
            const client = clients.find(client => client.clientId === targetClientId);

            if (client) {
                return client;
            }
        }

        return null;
    };

    const inflight = React.useRef<Promise<UserRecord> | null>(null);

    const fetchUser = React.useCallback(async () => {
        // *** SEE UserBootstrap for loading of user details!!! ***
        if (inflight.current) return inflight.current;

        const p = api.get<UserRecord>("user")
            .then(r => {
                const data = r.data;
                setUser(data);

                //Current Client Id
                const rawId = localStorage.getItem('activeClientId');

                const storedClientId = rawId !== null && !isNaN(Number(rawId))
                    ? Number(rawId)
                    : null;

                if (storedClientId) {
                    const exists = data?.clients?.some(c => c.clientId === storedClientId);

                    if (exists) {
                        setActiveClientId(storedClientId);
                        if (data.clients) { 
                            const clientInfo = getClientInfo(data.clients, storedClientId);
                            setActiveClient(clientInfo);
                        }

                        return data;
                    }
                }

                // keep activeClientId in sync if default changed
                const picked =
                    data.defaultClientId ??
                    data.clients?.[0]?.clientId ??
                    null;
                if (picked != null && picked !== activeClientId) {
                    setActiveClientId(picked);

                    if (data.clients) {
                        const clientInfo = getClientInfo(data.clients, picked);
                        setActiveClient(clientInfo);
                    }

                    try { localStorage.setItem("activeClientId", String(picked)); } catch { }
                }


                return data;
            })
            .finally(() => { inflight.current = null; });
        inflight.current = p;
        return p;
    }, [api, activeClientId, setActiveClientId]);

    const refreshUser = React.useCallback(() => fetchUser(), [fetchUser]);

    const scheduleRefresh = () => {
        if (debounceRef.current) {
            window.clearTimeout(debounceRef.current);
        }

        debounceRef.current = window.setTimeout(() => {
            refreshUser()
                .catch((err) => {
                    console.error("Failed to refresh user", err);
                })
                .finally(() => {
                    debounceRef.current = null;
                });
        }, 150);
    };

    const invalidateUser = () => {
        window.dispatchEvent(new Event("user:changed"));
        bcRef.current?.postMessage({ type: "user:changed" });
        scheduleRefresh();
    };

    const updateUserSettings = React.useCallback(
        async (settings: Partial<UserSettingsPreferencesDto>) => {

            // Capture current settings for rollback
            const previous: UserSettingsPreferencesDto = {
                compactView,
                showValues,
                dimensionUnitPreference,
                dimensionDisplayPreference
            };

            // Build complete settings object
            const updated: UserSettingsPreferencesDto = {
                ...previous,
                ...settings
            };

            // Optimistic local update
            setCompactView(updated.compactView);
            setShowValues(updated.showValues);
            setDimensionUnitPreference(updated.dimensionUnitPreference);
            setDimensionDisplayPreference(updated.dimensionDisplayPreference);

            // Keep UserRecord in sync as well
            setUser(current =>
                current
                    ? {
                        ...current,
                        ...updated
                    }
                    : current
            );

            try {
                await api.put("user/settings", updated);
            }
            catch (error) {
                // Roll back if persistence fails
                setCompactView(previous.compactView);
                setShowValues(previous.showValues);
                setDimensionUnitPreference(previous.dimensionUnitPreference);
                setDimensionDisplayPreference(previous.dimensionDisplayPreference);

                setUser(current =>
                    current
                        ? {
                            ...current,
                            ...previous
                        }
                        : current
                );

                throw error;
            }
        },
        [
            api,
            compactView,
            showValues,
            dimensionUnitPreference,
            dimensionDisplayPreference
        ]
    );
    React.useEffect(() => {

        setIsSysAdmin(false);

        if (user) {
            const sysRoles = user.system?.roles ?? [];
            const sysAdmin = sysRoles.some(r => r.startsWith("sys:"));
            setIsSysAdmin(sysAdmin);
            setCompactView(user.compactView ?? true);
            setShowValues(user.showValues ?? false);
            setDimensionUnitPreference(user.dimensionUnitPreference ?? DimensionUnitType.Metric);
            setDimensionDisplayPreference(user.dimensionDisplayPreference ?? DimensionDisplayType.Unframed);
        }

        setIsAdmin(false);
        setIsOwner(false);
        setIsValuer(false);
        setHasClientLevelRole(false);
        setIsFinance(false);
        setDefaultPortfolioId(0);

        const currentClient =
            user?.clients?.find(c => c.clientId === activeClientId) ?? null;

        const licenseAccess =
            currentClient?.licenseAccess ?? null;

        setActiveClient(currentClient);
        setActiveClientLicense(licenseAccess);

        if (currentClient) {
            setIsAdmin(currentClient.roleCode === "Admin");
            setIsOwner(currentClient.roleCode === "Owner");
            setIsValuer(currentClient.roleCode === "Valuer");
            setHasClientLevelRole(currentClient.hasClientWideRole);
            setDefaultPortfolioId(currentClient.defaultPortfolioId);

            setIsFinance(
                currentClient.roleCode === "Finance" ||
                currentClient.roleCode === "Admin" ||
                currentClient.roleCode === "Owner"
            );
        }

    }, [user, activeClientId]);

    const value: UserCtx = {
        getAccessToken,
        user, setUser,
        activeClientId, setActiveClientId, activeClient, activeClientLicense,
        activeTenantId, setActiveTenantId,
        refreshUser,
        invalidateUser,
        getClientInfo,
        setHasClientLevelRole, hasClientLevelRole,
        setDefaultPortfolioId, defaultPortfolioId,
        isAdmin,
        isOwner,
        isValuer,
        isSysAdmin,
        isFinance,
        isViewOnly, setIsViewOnly,
        compactView,
        showValues,
        dimensionDisplayPreference,
        dimensionUnitPreference,
        updateUserSettings
    };

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useUser = () => {
    const ctx = React.useContext(Ctx);
    if (!ctx) throw new Error("useUser must be used within a UserProvider");
    return ctx;
};
